import googleCalendarLogo from "@/assets/integrations/google-calendar.svg";
import CalendarIntegrationStoppedAlert from "@/components/integrations/calendar-integration-stopped-alert";
import DialogFakeCalendarEvent from "@/components/integrations/dialog-fake-calendar-event";
import IntegrationCalendarPromptSelector from "@/components/integrations/integration-calendar-prompt-selector";
import PageSkeleton from "@/components/page-skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import { useGoogleCalendarIntegration } from "@/contexts/google-calendar-integration-provider";
import useAccess from "@/hooks/useAccess";
import { useCalendarIntegrationPage } from "@/hooks/useCalendarIntegrationPage";
import { useTranslation } from "@/hooks/useTranslation";
import {
  ArrowLeft,
  ExternalLink,
  FlaskConical,
  LogOut,
  RefreshCw,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

type GoogleCalendar = {
  id: string;
  name: string;
  primary?: boolean;
  accessRole?: string;
};

export default function IntegrationGoogleCalendarPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasAccess } = useAccess();
  const { isAdminMode } = useAdminMode();

  const {
    googleCalendarIntegration,
    isLoading,
    refetch,
  } = useGoogleCalendarIntegration();

  const {
    calendars,
    isLoadingCalendars,
    isDisconnecting,
    showDisconnectDialog,
    setShowDisconnectDialog,
    showPromptDialog,
    setShowPromptDialog,
    showCreatePromptDialog,
    setShowCreatePromptDialog,
    isUpdatingPrompt,
    promptName,
    isSyncing,
    isCreatingFakeEvent,
    showFakeEventDialog,
    setShowFakeEventDialog,
    handleConnect,
    handleDisconnect,
    handleUpdateCalendar,
    handleUpdatePrompt,
    handleSync,
    handleCreateFakeEvent,
  } = useCalendarIntegrationPage<GoogleCalendar>({
    integration: googleCalendarIntegration,
    apiBasePath: "/integration/calendar/google-calendar",
    oauthEnvKey: "VITE_INTEGRATION_GOOGLE_CLOUD_CLIENT_ID",
    oauthUrl: "https://accounts.google.com/o/oauth2/v2/auth",
    oauthParams: (clientId) => ({
      client_id: clientId,
      redirect_uri: window.location.origin + window.location.pathname,
      response_type: "code",
      scope: [
        "https://www.googleapis.com/auth/calendar.readonly",
        "https://www.googleapis.com/auth/calendar.events.readonly",
      ].join(" "),
      access_type: "offline",
      prompt: "consent",
    }),
    refetch,
    i18nPrefix: "team.integrations.googleCalendar",
  });

  if (isLoading) {
    return <PageSkeleton className="p-4" />;
  }

  return (
    <div className="mt-8">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/integrations")}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common.back")}
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-4 space-y-0">
          <div className="flex items-center gap-3">
            <img
              src={googleCalendarLogo}
              alt="Google Calendar"
              className="w-6 h-6"
            />
            <CardTitle>{t("team.integrations.googleCalendar.title")}</CardTitle>
            {!googleCalendarIntegration && (
              <Badge variant="outline" className="text-xs">
                {t("team.integrations.googleCalendar.badge.notConnected")}
              </Badge>
            )}
            {googleCalendarIntegration && !googleCalendarIntegration.stoppedReason && (
              <Badge variant="success" className="text-xs">
                {t("team.integrations.googleCalendar.badge.connected")}
              </Badge>
            )}
            {googleCalendarIntegration?.stoppedReason && (
              <Badge variant="warning" className="text-xs">
                {t("team.integrations.calendar.stopped.badge")}
              </Badge>
            )}
          </div>
          {isAdminMode && googleCalendarIntegration && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFakeEventDialog(true)}
                disabled={isCreatingFakeEvent}
                className="flex items-center gap-2 border-warning dark:border-warning"
              >
                <FlaskConical className="w-4 h-4" />
                {t("team.integrations.calendar.fakeEvent.button")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSync}
                disabled={isSyncing}
                className="flex items-center gap-2 border-warning dark:border-warning"
              >
                <RefreshCw
                  className={`w-4 h-4 ${isSyncing ? "animate-spin" : ""}`}
                />
                {isSyncing
                  ? t("team.integrations.googleCalendar.sync.buttonLoading")
                  : t("team.integrations.calendar.sync.forceButton")}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {!googleCalendarIntegration && (
            <>
              <p className="text-muted-foreground">
                {t("team.integrations.googleCalendar.status.description")}
              </p>

              <div className="flex items-center gap-3">
                <Button
                  onClick={handleConnect}
                  className="flex items-center gap-2"
                  disabled={
                    !hasAccess({
                      requiredLicences: ["professional"],
                      licenceActiveState: true,
                    })
                  }
                >
                  <ExternalLink className="w-4 h-4" />
                  {t("team.integrations.googleCalendar.connect")}
                </Button>
                {!hasAccess({
                  requiredLicences: ["professional"],
                  licenceActiveState: true,
                }) && (
                  <Badge
                    variant="secondary"
                    className="flex gap-1.5 items-center px-3 py-1"
                  >
                    <Zap className="w-3.5 h-3.5 text-purple-500" />
                    <span className="text-xs">
                      {t(
                        "team.integrations.googleCalendar.connectTooltip.proOnly"
                      )}
                    </span>
                  </Badge>
                )}
              </div>
            </>
          )}

          {googleCalendarIntegration && (
            <div className="space-y-4">
              <CalendarIntegrationStoppedAlert
                stoppedReason={googleCalendarIntegration.stoppedReason}
              />
              <div className="flex flex-col gap-2">
                <p className="text-lg font-medium">
                  {t("team.integrations.googleCalendar.calendar.description")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "team.integrations.googleCalendar.calendar.descriptionDetail"
                  )}
                </p>
              </div>

              <div className="flex gap-4">
                {calendars.length > 0 && (
                  <div className="flex-1 space-y-2">
                    <label className="text-sm font-medium">
                      {t(
                        "team.integrations.googleCalendar.calendar.selectLabel"
                      )}
                    </label>
                    <Select
                      value={
                        googleCalendarIntegration.selectedCalendarId ||
                        undefined
                      }
                      onValueChange={handleUpdateCalendar}
                      disabled={isLoadingCalendars}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t(
                            "team.integrations.googleCalendar.calendar.placeholder"
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {calendars.map((calendar) => (
                          <SelectItem key={calendar.id} value={calendar.id}>
                            {calendar.name}
                            {calendar.primary && " (Primary)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <IntegrationCalendarPromptSelector
                  promptName={promptName}
                  isUpdatingPrompt={isUpdatingPrompt}
                  showPromptDialog={showPromptDialog}
                  showCreatePromptDialog={showCreatePromptDialog}
                  onPromptSelect={handleUpdatePrompt}
                  onShowPromptDialogChange={setShowPromptDialog}
                  onShowCreatePromptDialogChange={setShowCreatePromptDialog}
                />
              </div>

              <div className="flex justify-end">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDisconnectDialog(true)}
                  disabled={isDisconnecting}
                  className="flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  {isDisconnecting
                    ? t(
                        "team.integrations.googleCalendar.disconnect.buttonLoading"
                      )
                    : t("team.integrations.googleCalendar.disconnect.button")}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <DialogFakeCalendarEvent
        open={showFakeEventDialog}
        onOpenChange={setShowFakeEventDialog}
        onSubmit={handleCreateFakeEvent}
        isLoading={isCreatingFakeEvent}
      />

      <AlertDialog
        open={showDisconnectDialog}
        onOpenChange={setShowDisconnectDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                "team.integrations.googleCalendar.disconnect.confirmDialog.title"
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "team.integrations.googleCalendar.disconnect.confirmDialog.description"
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(
                "team.integrations.googleCalendar.disconnect.confirmDialog.cancel"
              )}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDisconnecting
                ? t("team.integrations.googleCalendar.disconnect.buttonLoading")
                : t(
                    "team.integrations.googleCalendar.disconnect.confirmDialog.confirm"
                  )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
